import {
  useQuery,
  UseQueryOptions,
  useMutation,
  UseMutationOptions,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { DocumentAgentAPI } from 'api/document-agent';
import {
  Company,
  DocumentAgentResponse,
  CreateCompanyPageResponse,
  CreateCompanyPageFromAgentPayload,
  AIContextsResponse,
  AIContextsPayload,
  DocumentAgentPayload,
  DocumentationAgentsResponse,
  DocumentationAgent,
} from 'api/document-agent/types';

export const useGetAllCompanies = (
  queryOptions?: Omit<
    UseQueryOptions<Company[], Error, Company[], string[]>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['companies', 'all'],
    queryFn: () => DocumentAgentAPI.getAllCompanies(),
    ...queryOptions,
  });
};

export const useCreateCompanyPage = (
  companyId: number,
  templateId: number,
  queryOptions?: Omit<
    UseQueryOptions<
      CreateCompanyPageResponse,
      Error,
      CreateCompanyPageResponse,
      (string | number)[]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['company-page', 'create', companyId, templateId],
    queryFn: () => DocumentAgentAPI.createCompanyPage(companyId, templateId),
    enabled: false, // Don't run automatically, only when manually triggered
    ...queryOptions,
  });
};

export const useDocumentationAgent = (
  companyId: number,
  payload: DocumentAgentPayload,
  queryOptions?: Omit<
    UseQueryOptions<
      DocumentAgentResponse,
      Error,
      DocumentAgentResponse,
      (string | number | DocumentAgentPayload)[]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: [
      'documentation-agent',
      companyId,
      payload.configuration.templateId,
      payload,
    ],
    queryFn: () => DocumentAgentAPI.getDocumentationAgent(companyId, payload),
    enabled: false, // Don't run automatically, only when manually triggered
    ...queryOptions,
  });
};

export const useGetAdditionalAIContexts = (
  documentAgentId: string,
  queryOptions?: Omit<
    UseQueryOptions<
      AIContextsResponse,
      Error,
      AIContextsResponse,
      (string | string[])[]
    >,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: ['additional-ai-contexts', documentAgentId],
    queryFn: () => DocumentAgentAPI.getAdditionalAIContexts(documentAgentId),
    ...queryOptions,
  });
};

export const useUpdateAdditionalAIContexts = (
  options?: UseMutationOptions<
    AIContextsResponse,
    Error,
    { documentAgentId: string; payload: AIContextsPayload }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentAgentId,
      payload,
    }: {
      documentAgentId: string;
      payload: AIContextsPayload;
    }) => DocumentAgentAPI.updateAdditionalAIContexts(documentAgentId, payload),
    onSuccess: (data, variables) => {
      // Set the response as new data immediately
      queryClient.setQueryData(
        ['additional-ai-contexts', variables.documentAgentId],
        data,
      );
      // Refetch to ensure we have the latest server data
      queryClient.invalidateQueries({
        queryKey: ['additional-ai-contexts', variables.documentAgentId],
      });
    },
    ...options,
  });
};

export const useDeleteAdditionalAIContext = (
  options?: UseMutationOptions<
    void,
    Error,
    { documentAgentId: string; templateSectionId: string }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentAgentId,
      templateSectionId,
    }: {
      documentAgentId: string;
      templateSectionId: string;
    }) =>
      DocumentAgentAPI.deleteAdditionalAIContext(
        documentAgentId,
        templateSectionId,
      ),
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the contexts after deletion
      queryClient.invalidateQueries({
        queryKey: ['additional-ai-contexts', variables.documentAgentId],
      });
    },
    ...options,
  });
};

export const useDeleteQuestionAIContext = (
  options?: UseMutationOptions<
    void,
    Error,
    {
      documentAgentId: string;
      templateSectionId: string;
      nodeId: string;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentAgentId,
      templateSectionId,
      nodeId,
    }: {
      documentAgentId: string;
      templateSectionId: string;
      nodeId: string;
    }) =>
      DocumentAgentAPI.deleteQuestionAIContext(
        documentAgentId,
        templateSectionId,
        nodeId,
      ),
    onSuccess: (_data, variables) => {
      // Invalidate and refetch the contexts after deletion
      queryClient.invalidateQueries({
        queryKey: ['additional-ai-contexts', variables.documentAgentId],
      });
    },
    ...options,
  });
};

export const useUpdateQuestionAIContext = (
  options?: UseMutationOptions<
    AIContextsResponse,
    Error,
    {
      documentAgentId: string;
      templateSectionId: string;
      nodeId: string;
      payload: AIContextsPayload;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentAgentId,
      templateSectionId,
      nodeId,
      payload,
    }: {
      documentAgentId: string;
      templateSectionId: string;
      nodeId: string;
      payload: AIContextsPayload;
    }) =>
      DocumentAgentAPI.updateQuestionAIContext(
        documentAgentId,
        templateSectionId,
        nodeId,
        payload,
      ),
    onSuccess: (data, variables) => {
      // Set the response as new data immediately
      queryClient.setQueryData(
        ['additional-ai-contexts', variables.documentAgentId],
        data,
      );
      // Refetch to ensure we have the latest server data
      queryClient.invalidateQueries({
        queryKey: ['additional-ai-contexts', variables.documentAgentId],
      });
    },
    ...options,
  });
};

const DOCUMENTATION_AGENTS_KEY = (
  limit: number,
  offset: number,
  creationTrigger: string,
) =>
  ['documentation-agents', 'search', limit, offset, creationTrigger] as const;

const DOCUMENTATION_AGENT_BY_ID_KEY = (documentAgentId: number) =>
  ['documentation-agent', documentAgentId] as const;

/**
 * Hook to search documentation agents with pagination (default: MANUAL creation trigger).
 */
export const useGetDocumentationAgentsQuery = (
  limit: number = 6,
  offset: number = 0,
  creationTrigger: string = 'MANUAL',
  queryOptions?: Omit<
    UseQueryOptions<
      DocumentationAgentsResponse,
      Error,
      DocumentationAgentsResponse,
      ReturnType<typeof DOCUMENTATION_AGENTS_KEY>
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<DocumentationAgentsResponse, Error> => {
  return useQuery({
    queryKey: DOCUMENTATION_AGENTS_KEY(limit, offset, creationTrigger),
    queryFn: () =>
      DocumentAgentAPI.searchDocumentationAgents(
        limit,
        offset,
        creationTrigger,
      ),
    ...queryOptions,
  });
};

/**
 * Hook to get a single documentation agent by ID
 */
export const useGetDocumentationAgentQuery = (
  documentAgentId: number,
  queryOptions?: Omit<
    UseQueryOptions<
      DocumentationAgent,
      Error,
      DocumentationAgent,
      ReturnType<typeof DOCUMENTATION_AGENT_BY_ID_KEY>
    >,
    'queryKey' | 'queryFn'
  >,
): UseQueryResult<DocumentationAgent, Error> => {
  return useQuery({
    queryKey: DOCUMENTATION_AGENT_BY_ID_KEY(documentAgentId),
    queryFn: () => DocumentAgentAPI.getDocumentationAgentById(documentAgentId),
    ...queryOptions,
  });
};

/**
 * Mutation hook to update documentation agent (sends entire object)
 * Composes onSuccess so internal cache updates always run, then the caller's onSuccess (e.g. navigate) is called.
 */
export const useUpdateDocumentationAgentMutation = (
  options?: UseMutationOptions<DocumentationAgent, Error, DocumentationAgent>,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: DocumentationAgent) =>
      DocumentAgentAPI.updateDocumentationAgent(payload),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.setQueryData(DOCUMENTATION_AGENT_BY_ID_KEY(data.id), data);
      queryClient.invalidateQueries({ queryKey: ['documentation-agents'] });
      (options?.onSuccess as any)?.(data, variables, context);
    },
  });
};

/**
 * Mutation hook to create company page from documentation agent
 */
export const useCreateCompanyPageFromAgent = (
  options?: UseMutationOptions<
    CreateCompanyPageResponse,
    Error,
    {
      sourceId: number;
      documentAgentId?: number;
      payload?: CreateCompanyPageFromAgentPayload;
      confirmed?: boolean;
      objectType?: 'COMPANY' | 'PROJECT';
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      sourceId,
      documentAgentId,
      payload,
      confirmed,
      objectType,
    }: {
      sourceId: number;
      documentAgentId?: number;
      payload?: CreateCompanyPageFromAgentPayload;
      confirmed?: boolean;
      objectType?: 'COMPANY' | 'PROJECT';
    }) =>
      DocumentAgentAPI.createCompanyPageFromAgent(
        sourceId,
        documentAgentId ?? 0,
        payload,
        confirmed,
        objectType,
      ),
    onSuccess: () => {
      // Invalidate relevant queries after successful creation
      queryClient.invalidateQueries({
        queryKey: ['company-page'],
      });
    },
    ...options,
  });
};

/**
 * Mutation hook to update company page sources
 */
export const useUpdateCompanyPageSources = (
  options?: UseMutationOptions<
    void,
    Error,
    {
      documentId: number;
      payload: {
        EMAIL?: number[];
        MEETING?: number[];
      };
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      documentId,
      payload,
    }: {
      documentId: number;
      payload: {
        EMAIL?: number[];
        MEETING?: number[];
      };
    }) => DocumentAgentAPI.updateCompanyPageSources(documentId, payload),
    onSuccess: (_data, variables) => {
      // Invalidate document query to refetch after updating sources
      queryClient.invalidateQueries({
        queryKey: ['document', variables.documentId],
      });
    },
    ...options,
  });
};
